/**
 * Decision Brief — Proposal Submission (Public, token-gated)
 *
 * Route: /brief/submit/:token  —  No auth required.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { getOptionBySubmitToken, submitBriefProposal } from '../../services/brief/briefService';
import type { SubmitTokenView } from '../../types/brief';
import { splitOpportunityCost, joinOpportunityCost } from './lib/opportunityCost';

const inputCls =
  'w-full border border-[var(--rule-bright)]  px-3 py-2 text-sm text-[var(--text-pri)] bg-[var(--surface)] placeholder:text-[var(--text-dim)] focus:outline-none focus:ring-2 focus:ring-[var(--slate)] focus:border-transparent transition-shadow';

const labelCls = 'block text-sm font-medium text-[var(--text-sec)] mb-1';
const hintCls = 'text-xs text-[var(--text-ter)] mt-1';

function AssumptionRow({ value, onChange, onRemove, placeholder, canRemove }: {
  value: string; onChange: (v: string) => void; onRemove: () => void;
  placeholder: string; canRemove: boolean;
}) {
  return (
    <div className="flex items-start gap-2">
      <span className="flex-shrink-0 text-[var(--text-dim)] text-sm mt-2">·</span>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`${inputCls} flex-1`} />
      {canRemove && (
        <button type="button" onClick={onRemove} className="flex-shrink-0 text-[var(--text-dim)] hover:text-[var(--coral)] mt-2 text-xs px-1">✕</button>
      )}
    </div>
  );
}

function SubmittedState({ optionName, briefTitle, deadline }: { optionName: string; briefTitle: string; deadline: string | null }) {
  const due = deadline
    ? new Date(deadline + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
    : null;
  return (
    <div className="min-h-screen bg-[var(--bg)] flex items-center justify-center px-6">
      <div className="max-w-sm text-center">
        <div className="w-12 h-12 rounded-full bg-[var(--green)]/10 flex items-center justify-center mx-auto mb-4">
          <CheckCircleIcon className="w-6 h-6 text-[var(--green)]" />
        </div>
        <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-2">Proposal submitted</h2>
        <p className="text-sm text-[var(--text-sec)]">
          Your proposal for <strong className="text-[var(--text-pri)]">{optionName}</strong> has been submitted to the{' '}
          <strong className="text-[var(--text-pri)]">{briefTitle}</strong> brief.
        </p>
        <p className="text-sm text-[var(--text-sec)] mt-4">
          {due ? `The decision is due ${due}. ` : ''}Whoever is deciding will compare every proposal once they are in, and this decision's record link will show the outcome.
        </p>
        <p className="text-xs text-[var(--text-ter)] mt-2">You are done here. You can close this tab.</p>
      </div>
    </div>
  );
}

export default function BriefSubmit() {
  const { token } = useParams<{ token: string }>();

  const [view, setView] = useState<SubmitTokenView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  const [rationale, setRationale] = useState('');
  const [investment, setInvestment] = useState('');
  const [outcome, setOutcome] = useState('');
  const [assumptions, setAssumptions] = useState(['', '']);
  const [constraints, setConstraints] = useState('');
  const [opportunity, setOpportunity] = useState('');
  const [risk, setRisk] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!token) return;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await getOptionBySubmitToken(token!);
        if (!data) { setError('This submission link is invalid or has expired.'); return; }
        setView(data);
        if (data.existing_proposal) {
          const p = data.existing_proposal;
          setRationale(p.rationale ?? '');
          setInvestment(p.investment_summary ?? '');
          setOutcome(p.expected_outcome ?? '');
          setAssumptions(p.assumptions?.length ? p.assumptions : ['', '']);
          const split = splitOpportunityCost(p.opportunity_cost);
          setConstraints(split.constraints);
          setOpportunity(split.opportunity);
          setRisk(p.top_risk ?? '');
          setNotes(p.additional_notes ?? '');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load submission form');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !view) return;
    setSaving(true);
    setError(null);
    try {
      await submitBriefProposal(token, {
        rationale, investment, outcome,
        assumptions: assumptions.filter((a) => a.trim()),
        opportunity: joinOpportunityCost(opportunity, constraints),
        risk, notes: notes || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit proposal');
      setSaving(false);
    }
  };

  if (submitted && view) return <SubmittedState optionName={view.name} briefTitle={view.brief_title} deadline={view.deadline} />;

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg)] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[var(--rule-bright)] border-t-[var(--text-sec)] rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !view) {
    return (
      <div className="min-h-screen bg-[var(--bg)] flex items-center justify-center px-6">
        <div className="max-w-sm text-center">
          <h2 className="text-base font-medium text-[var(--text-pri)] mb-2">Link unavailable</h2>
          <p className="text-sm text-[var(--text-sec)]">{error}</p>
        </div>
      </div>
    );
  }

  if (!view) return null;

  // Mirrors submit_brief_proposal (migration 793): submissions close the moment comparing starts.
  const isClosed = view.status !== 'collecting';
  if (isClosed) {
    return (
      <div className="min-h-screen bg-[var(--bg)] flex items-center justify-center px-6">
        <div className="max-w-sm text-center">
          <h2 className="text-base font-medium text-[var(--text-pri)] mb-2">Brief closed</h2>
          <p className="text-sm text-[var(--text-sec)]">
            The <strong className="text-[var(--text-pri)]">{view.brief_title}</strong> brief is no longer accepting submissions.
          </p>
        </div>
      </div>
    );
  }

  const deadline = view.deadline
    ? new Date(view.deadline + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <div className="bg-[var(--bg-raised)] border-b border-[var(--rule)]">
        <div className="max-w-2xl mx-auto px-6 py-5">
          <p className="text-xs text-[var(--text-ter)] uppercase tracking-wide mb-1">Decision Brief</p>
          <h1 className="text-lg font-semibold text-[var(--text-pri)]">{view.brief_title}</h1>
          {view.brief_context && <p className="text-sm text-[var(--text-sec)] mt-1">{view.brief_context}</p>}
          {deadline && <p className="text-xs text-[var(--text-ter)] mt-2">Decision deadline: {deadline}</p>}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-8">
        <div className="border border-[var(--rule)] bg-[var(--surface)] p-4 mb-6">
          <p className="text-xs text-[var(--text-ter)] uppercase tracking-wide mb-1">You are submitting a proposal for</p>
          <p className="font-medium text-[var(--text-pri)]">{view.name}</p>
          {view.owner_name && <p className="text-sm text-[var(--text-sec)] mt-0.5">Owner: {view.owner_name}</p>}
        </div>

        {view.criteria?.length > 0 && (
          <div className="mb-6">
            <p className="text-xs text-[var(--text-ter)] uppercase tracking-wide mb-2">How your proposal will be evaluated</p>
            <div className="space-y-2">
              {view.criteria.map((c) => (
                <div key={c.id} className="flex items-start gap-3 text-sm">
                  <span className="flex-shrink-0 text-[var(--text-dim)] mt-0.5">·</span>
                  <div>
                    <span className="font-medium text-[var(--text-sec)]">{c.name}</span>
                    {c.description && <span className="text-[var(--text-ter)]"> — {c.description}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="border border-[var(--coral)]/30 bg-[var(--coral)]/10 px-3 py-2 text-sm text-[var(--coral)]">{error}</div>
          )}

          <div>
            <label className={labelCls}>Strategic rationale <span className="text-[var(--coral)]">*</span></label>
            <textarea value={rationale} onChange={(e) => setRationale(e.target.value)} required rows={3}
              placeholder="Why should we choose this? What strategic problem does it solve?" className={inputCls} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Investment required</label>
              <input type="text" value={investment} onChange={(e) => setInvestment(e.target.value)}
                placeholder="e.g. 3 engineers, $400k, 9 months" className={inputCls} />
              <p className={hintCls}>People, budget, time — be specific</p>
            </div>
            <div>
              <label className={labelCls}>Expected outcome</label>
              <input type="text" value={outcome} onChange={(e) => setOutcome(e.target.value)}
                placeholder="e.g. 15% reduction in churn by Q4" className={inputCls} />
              <p className={hintCls}>Measurable result, not activity</p>
            </div>
          </div>

          <div>
            <label className={labelCls}>Key assumptions</label>
            <p className={hintCls + ' mb-2'}>What must be true for this to work?</p>
            <div className="space-y-2">
              {assumptions.map((a, i) => (
                <AssumptionRow key={i} value={a}
                  onChange={(v) => { const next = [...assumptions]; next[i] = v; setAssumptions(next); }}
                  onRemove={() => setAssumptions(assumptions.filter((_, j) => j !== i))}
                  placeholder={i === 0 ? 'e.g. We can hire two ML engineers by March' : i === 1 ? 'e.g. Partner API remains stable' : 'Another assumption...'}
                  canRemove={assumptions.length > 1}
                />
              ))}
            </div>
            {assumptions.length < 4 && (
              <button type="button" onClick={() => setAssumptions([...assumptions, ''])}
                className="mt-2 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors">
                + Add assumption
              </button>
            )}
          </div>

          <div>
            <label className={labelCls}>Constraints on this option</label>
            <textarea value={constraints} onChange={(e) => setConstraints(e.target.value)} rows={2}
              placeholder="What conditions must be true for this option to work? What would cause it to fail regardless of intent?"
              className={inputCls} />
            <p className={hintCls}>
              The inverse of assumptions — not what must be true, but what conditions would make this option unviable even if chosen.
              The decision-maker needs to evaluate these explicitly.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Opportunity cost</label>
              <textarea value={opportunity} onChange={(e) => setOpportunity(e.target.value)} rows={2}
                placeholder="What does the org forgo by choosing this?" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Top risk</label>
              <textarea value={risk} onChange={(e) => setRisk(e.target.value)} rows={2}
                placeholder="The single most likely failure mode" className={inputCls} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Additional notes <span className="text-[var(--text-dim)] font-normal">(optional)</span></label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              placeholder="Anything else the decision-maker should know" className={inputCls} />
          </div>

          <div className="pt-2">
            <button type="submit" disabled={saving || !rationale.trim()}
              className="w-full py-2.5 bg-[var(--text-pri)] text-[var(--bg)] text-sm font-medium hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity">
              {saving ? 'Submitting...' : view.existing_proposal ? 'Update proposal' : 'Submit proposal'}
            </button>
            <p className="text-xs text-center text-[var(--text-ter)] mt-2">
              You can update your proposal any time before the brief closes.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
